// Menú — sección de la tab-bar inferior (rejilla). Agrupa los módulos
// secundarios del sistema (racks, analítica, auditoría, administración…) y la
// gestión de sesión del usuario (perfil / cerrar sesión). Las entradas respetan
// permisos (Req. 19.10): las secciones sin permiso se ocultan; la administración
// es exclusiva del propietario.

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext.js';
import type { Feature } from '../types.js';
import { Icon, PageHeader } from '../ui/components.js';
import { LanguageSelector } from '../ui/LanguageSelector.js';

interface ModuleEntry {
  to: string;
  /** Rótulo literal de respaldo (opcional; se prefiere `labelKey`). */
  label?: string;
  description?: string;
  /** Claves i18n del rótulo y la descripción (fuente principal de los textos). */
  labelKey?: string;
  descriptionKey?: string;
  icon: string;
  feature: Feature | null;
  /** Solo el propietario del tenant (o admin de sistema). */
  ownerOnly?: boolean;
  /** Exclusivo del administrador global de la plataforma (system_admin). */
  systemAdminOnly?: boolean;
}

/**
 * Módulos agrupados por categoría dentro del Menú, en el orden pedido:
 *   1) Herramientas de Catálogo  2) Configuraciones de Catálogo
 *   3) Ayuda & Cuenta            4) Administración (solo system_admin)
 * Cada entrada respeta permisos (feature / ownerOnly / systemAdminOnly); el
 * grupo "Administración" entero solo lo ve el administrador global.
 */
const MODULE_GROUPS: { group: string; groupKey: string; items: ModuleEntry[] }[] = [
  {
    group: 'Herramientas de Catálogo',
    groupKey: 'menu.groups.catalogTools',
    items: [
      {
        to: '/audio',
        labelKey: 'menu.items.audioLabel',
        descriptionKey: 'menu.items.audioDesc',
        icon: 'mic',
        feature: 'quick_lookup',
      },
      {
        to: '/store-locator',
        labelKey: 'menu.items.storeLocatorLabel',
        descriptionKey: 'menu.items.storeLocatorDesc',
        icon: 'storefront',
        feature: 'quick_lookup',
      },
      {
        to: '/duplicates',
        labelKey: 'menu.items.duplicatesLabel',
        descriptionKey: 'menu.items.duplicatesDesc',
        icon: 'content_copy',
        feature: 'duplicates',
      },
      {
        to: '/top',
        labelKey: 'menu.items.topLabel',
        descriptionKey: 'menu.items.topDesc',
        icon: 'star',
        feature: 'top',
      },
      {
        to: '/sync',
        labelKey: 'menu.items.syncLabel',
        descriptionKey: 'menu.items.syncDesc',
        icon: 'sync',
        feature: 'sync',
      },
      {
        to: '/analytics',
        labelKey: 'menu.items.analyticsLabel',
        descriptionKey: 'menu.items.analyticsDesc',
        icon: 'monitoring',
        feature: 'catalog',
      },
      {
        to: '/config/deal-hunter',
        labelKey: 'menu.items.dealHunterLabel',
        descriptionKey: 'menu.items.dealHunterDesc',
        icon: 'travel_explore',
        feature: 'deal_hunter',
        ownerOnly: true,
      },
    ],
  },
  {
    group: 'Configuraciones de Catálogo',
    groupKey: 'menu.groups.catalogConfig',
    items: [
      {
        to: '/locations',
        labelKey: 'menu.items.locationsLabel',
        descriptionKey: 'menu.items.locationsDesc',
        icon: 'shelves',
        feature: 'locations',
      },
      {
        to: '/taxonomy',
        labelKey: 'menu.items.taxonomyLabel',
        descriptionKey: 'menu.items.taxonomyDesc',
        icon: 'category',
        feature: 'catalog',
      },
      {
        to: '/config/formats',
        labelKey: 'menu.items.formatsLabel',
        descriptionKey: 'menu.items.formatsDesc',
        icon: 'filter_list',
        feature: 'user_admin',
        ownerOnly: true,
      },
      {
        to: '/config/sync-rules',
        labelKey: 'menu.items.syncRulesLabel',
        descriptionKey: 'menu.items.syncRulesDesc',
        icon: 'rule',
        feature: 'user_admin',
        ownerOnly: true,
      },
      {
        to: '/config/fields',
        labelKey: 'menu.items.fieldsLabel',
        descriptionKey: 'menu.items.fieldsDesc',
        icon: 'dynamic_form',
        feature: 'user_admin',
        ownerOnly: true,
      },
      {
        to: '/config/curation-rules',
        labelKey: 'menu.items.curationLabel',
        descriptionKey: 'menu.items.curationDesc',
        icon: 'rule_settings',
        feature: 'user_admin',
        ownerOnly: true,
      },
      {
        to: '/data',
        labelKey: 'menu.items.dataLabel',
        descriptionKey: 'menu.items.dataDesc',
        icon: 'database',
        feature: 'catalog',
        ownerOnly: true,
      },
    ],
  },
  {
    group: 'Ayuda & Cuenta',
    groupKey: 'menu.groups.helpAccount',
    items: [
      {
        to: '/support',
        labelKey: 'menu.items.supportLabel',
        descriptionKey: 'menu.items.supportDesc',
        icon: 'support_agent',
        feature: null,
      },
      {
        to: '/guides',
        labelKey: 'menu.items.guidesLabel',
        descriptionKey: 'menu.items.guidesDesc',
        icon: 'menu_book',
        feature: null,
      },
      {
        to: '/admin',
        labelKey: 'menu.items.adminUsersLabel',
        descriptionKey: 'menu.items.adminUsersDesc',
        icon: 'admin_panel_settings',
        feature: 'user_admin',
        ownerOnly: true,
      },
      {
        to: '/audit',
        labelKey: 'menu.items.auditLabel',
        descriptionKey: 'menu.items.auditDesc',
        icon: 'fact_check',
        feature: 'catalog',
        ownerOnly: true,
      },
      {
        to: '/billing',
        labelKey: 'menu.items.billingLabel',
        descriptionKey: 'menu.items.billingDesc',
        icon: 'workspace_premium',
        feature: null,
        ownerOnly: true,
      },
      {
        to: '/account',
        labelKey: 'menu.items.accountLabel',
        descriptionKey: 'menu.items.accountDesc',
        icon: 'account_circle',
        feature: null,
      },
      {
        to: '/account/password',
        labelKey: 'menu.items.passwordLabel',
        descriptionKey: 'menu.items.passwordDesc',
        icon: 'password',
        feature: null,
      },
    ],
  },
  {
    group: 'Administración',
    groupKey: 'menu.groups.admin',
    items: [
      {
        to: '/admin/accounts',
        labelKey: 'menu.items.accountsLabel',
        descriptionKey: 'menu.items.accountsDesc',
        icon: 'manage_accounts',
        feature: null,
        systemAdminOnly: true,
      },
      {
        to: '/admin/service-cases',
        labelKey: 'menu.items.serviceCasesLabel',
        descriptionKey: 'menu.items.serviceCasesDesc',
        icon: 'confirmation_number',
        feature: null,
        systemAdminOnly: true,
      },
      {
        to: '/admin/plans',
        labelKey: 'menu.items.plansLabel',
        descriptionKey: 'menu.items.plansDesc',
        icon: 'workspace_premium',
        feature: null,
        systemAdminOnly: true,
      },
      {
        to: '/admin/legal',
        labelKey: 'menu.items.legalLabel',
        descriptionKey: 'menu.items.legalDesc',
        icon: 'gavel',
        feature: null,
        systemAdminOnly: true,
      },
      {
        to: '/admin/analytics',
        labelKey: 'menu.items.globalAnalyticsLabel',
        descriptionKey: 'menu.items.globalAnalyticsDesc',
        icon: 'monitoring',
        feature: null,
        systemAdminOnly: true,
      },
      {
        to: '/admin/deal-hunter',
        labelKey: 'menu.items.globalDealHunterLabel',
        descriptionKey: 'menu.items.globalDealHunterDesc',
        icon: 'travel_explore',
        feature: null,
        systemAdminOnly: true,
      },
      {
        to: '/admin/locales',
        labelKey: 'adminLocales.menuLabel',
        descriptionKey: 'adminLocales.menuDescription',
        icon: 'language',
        feature: null,
        systemAdminOnly: true,
      },
      {
        to: '/admin/logs',
        labelKey: 'menu.items.globalLogsLabel',
        descriptionKey: 'menu.items.globalLogsDesc',
        icon: 'receipt_long',
        feature: null,
        systemAdminOnly: true,
      },
    ],
  },
];

export function MenuView() {
  const { t } = useTranslation();
  const { username, permissions, hasPermission, isOwner, isSystemAdmin } = useAuth();

  const canSee = useMemo(
    () => (entry: ModuleEntry) => {
      if (entry.systemAdminOnly && !isSystemAdmin()) return false;
      if (entry.ownerOnly && !isOwner()) return false;
      if (entry.feature === null) return true;
      return hasPermission(entry.feature, 'read');
    },
    [hasPermission, isOwner, isSystemAdmin],
  );

  const groups = MODULE_GROUPS.map((g) => ({ ...g, items: g.items.filter(canSee) })).filter(
    (g) => g.items.length > 0,
  );

  const displayName = permissions?.displayName ?? username ?? t('menu.defaultUser');
  const email = permissions?.email ?? null;
  const roleLabel = permissions?.role === 'owner' ? t('menu.roleOwner') : t('menu.roleMember');
  const initial = (displayName || 'U').trim().charAt(0).toUpperCase();
  const avatarUrl = permissions?.avatarUrl ?? null;

  return (
    <div className="flex flex-col">
      <PageHeader
        eyebrow={t('menu.eyebrow')}
        title={t('menu.title')}
        subtitle={t('menu.subtitle')}
      />

      {/* Tarjeta de sesión / perfil */}
      <div className="px-margin-mobile py-space-md">
        <div className="surface-card flex items-center gap-space-md">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={t('menu.avatarAlt')}
              className="w-14 h-14 shrink-0 rounded-full object-cover border border-primary-container/40"
            />
          ) : (
            <span className="w-14 h-14 shrink-0 rounded-full bg-primary-container/20 border border-primary-container/40 text-primary flex items-center justify-center font-headline-md text-headline-md font-bold">
              {initial}
            </span>
          )}
          <div className="flex-1 min-w-0 flex flex-col gap-0.5">
            <span className="font-headline-sm text-headline-sm text-on-surface truncate">
              {displayName}
            </span>
            {email && (
              <span className="font-code-catalog text-body-sm text-on-surface-variant truncate">
                {email}
              </span>
            )}
            <span className="mt-0.5 inline-flex w-fit items-center gap-1 px-2 py-0.5 rounded-full bg-surface-rack text-vacuum-glow font-badge-micro text-badge-micro uppercase tracking-widest">
              <Icon name="verified_user" className="text-[13px]" />
              {roleLabel}
            </span>
          </div>
        </div>
      </div>

      {/* Selector de idioma de la app (i18n). El cambio se guarda en la cuenta
          del usuario y prevalece en los siguientes inicios de sesión. Se muestra
          aquí (hub principal) además de en "Mi cuenta" para que el cambio de
          idioma sea descubrible de un vistazo. */}
      <div className="px-margin-mobile pb-space-md">
        <div className="surface-card flex items-center justify-between gap-space-sm">
          <span className="flex items-center gap-space-xs font-body-sm text-body-sm text-on-surface-variant">
            <Icon name="language" className="text-primary text-[18px]" />
            {t('menu.languageLabel')}
          </span>
          <LanguageSelector />
        </div>
      </div>

      {/* Módulos por categoría. Cada grupo se separa visualmente del anterior
          con una línea divisoria y un encabezado destacado (ámbar, en negrilla y
          mayor tamaño) para que las subsecciones se distingan de un vistazo. */}
      <div className="px-margin-mobile pb-space-lg flex flex-col gap-space-xl">
        {groups.map((g, i) => (
          <section key={g.group} className="flex flex-col gap-space-md">
            {/* Separador entre subsecciones (desde la segunda en adelante). */}
            {i > 0 && <hr className="border-0 border-t border-border-subtle -mt-space-sm" />}
            <div className="flex items-center gap-space-sm">
              <h2 className="font-headline-sm text-headline-sm font-bold uppercase tracking-widest text-vacuum-glow">
                {t(g.groupKey)}
              </h2>
              <span className="flex-1 h-px bg-gradient-to-r from-vacuum-glow/40 to-transparent" />
            </div>
            <div className="grid grid-cols-2 gap-space-sm">
              {g.items.map((entry) => (
                <Link
                  key={entry.to}
                  to={entry.to}
                  className="surface-card flex flex-col gap-space-xs !p-space-md active:scale-95 transition-transform"
                >
                  {/* Icono + título en la misma línea; descripción debajo. */}
                  <div className="flex items-center gap-space-sm">
                    <span className="w-10 h-10 shrink-0 rounded-lg bg-surface-container-high flex items-center justify-center text-primary">
                      <Icon name={entry.icon} className="text-[20px]" />
                    </span>
                    <span className="font-body-md text-body-md font-bold text-on-surface leading-tight">
                      {entry.labelKey ? t(entry.labelKey) : entry.label}
                    </span>
                  </div>
                  <span className="font-body-sm text-body-sm text-on-surface-variant leading-tight">
                    {entry.descriptionKey ? t(entry.descriptionKey) : entry.description}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        ))}

        {groups.length === 0 && (
          <p className="surface-card text-center text-on-surface-variant font-body-sm text-body-sm py-space-lg">
            {t('menu.noModules')}
          </p>
        )}
      </div>
    </div>
  );
}
